/**
 * NavigationStateManager - Manages return URLs for edit pages using session storage
 * with security validations to prevent malicious redirects
 */
class NavigationStateManager {
  static isValidReturnUrl(url) {
    if (!url) return false;
    
    try {
      // Parse the URL to validate it
      const urlObj = new URL(url, window.location.origin);
      
      // 1. Must be same origin (protocol + host + port)
      if (urlObj.origin !== window.location.origin) {
        console.warn('Return URL rejected: different origin', url);
        return false;
      }
      
      // 2. Must be HTTP/HTTPS (no javascript:, data:, etc.)
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        console.warn('Return URL rejected: invalid protocol', url);
        return false;
      }
      
      // 3. Prevent iframe breakout attempts (no # or javascript in fragments)
      if (urlObj.hash.includes('javascript:') || urlObj.hash.includes('data:')) {
        console.warn('Return URL rejected: suspicious hash', url);
        return false;
      }
      
      // 4. Basic path validation - must start with /
      if (!urlObj.pathname.startsWith('/')) {
        console.warn('Return URL rejected: invalid path', url);
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Return URL rejected: parsing error', error);
      return false;
    }
  }
  
  static setReturnUrl(returnUrl) {
    // Validate before storing
    if (!this.isValidReturnUrl(returnUrl)) {
      return false;
    }
    
    const key = 'edit-return-url';
    sessionStorage.setItem(key, returnUrl);
    return true;
  }
  
  static setCurrentPageAsReturnUrl() {
    const currentUrl = window.location.pathname + window.location.search;
    return this.setReturnUrl(currentUrl);
  }
  
  static getReturnUrl() {
    const key = 'edit-return-url';
    const url = sessionStorage.getItem(key);
    // Validate on retrieval as well (defense in depth)
    if (url && this.isValidReturnUrl(url)) {
      return url;
    }
    if (url) {
      console.warn('Stored return URL failed validation, removing:', url);
      sessionStorage.removeItem(key);
    }
    return null;
  }

  static consumeReturnUrl() {
    const key = 'edit-return-url';
    const url = this.getReturnUrl();
    if (url) {
      sessionStorage.removeItem(key);
    }
    return url;
  }
  
  static clearReturnUrl() {
    const key = 'edit-return-url';
    sessionStorage.removeItem(key);
  }
  
  // Additional security: prevent storing URLs from different windows/tabs
  static getCurrentPageUrl() {
    // Use window.location directly to prevent manipulation
    return window.location.pathname + window.location.search;
  }
}

export default NavigationStateManager;
